// App Dashboard Page - Displays CAB details for application
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  Badge as BadgeIcon,
  Timeline as TimelineIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Person2Outlined,
  VisibilityOutlined as ScrutinyIcon,
  History as HistoryIcon,
  Person,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getApplicationStage } from "../../../services/api";
import DoApiService from "../../../services/DoApiService";
import {
  decryptAppId,
  decryptText,
  encryptAppId,
  encryptText,
} from "../../../utils/encryption";
import ReportsDetails from "../../Do/application/ReportsDetails";
import PaymentDetails from "../../Do/application/PaymentDetails";
import EmailNotificationDetails from "../../Do/application/EmailNotificationDetails";
import AssessorDetailsView from "./AssessorDetailsView";
import acProcessService from "../../../services/acProcessService";
import { generateCommunicationLetter } from "../../../components/certificate/CommunicationLetterGenerator";
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import { toast } from "react-toastify";
import { generateCertificateOfAccreditation } from "../../../components/certificate/CertificateGenerator";
import { generateScopeOfAccreditation } from "../../../components/certificate/ScopeGenerator";
import { generateSCFAnnexure } from "../../../components/certificate/SCFAnnexureGenerator";
import dayjs from "dayjs";

const AppDashboard = () => {
  // State management
  const [cabDetails, setCabDetails] = useState(null);
  const [dealingOfficer, setDealingOfficer] = useState(null);
  const [contactPersons, setContactPersons] = useState([]); // Changed to array for multiple contacts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOfficerDialog, setOpenOfficerDialog] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);

  // CEO Approve/Disapprove states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [disapproveDialogOpen, setDisapproveDialogOpen] = useState(false);
  const [ceoPassword, setCeoPassword] = useState("");
  const [disapprovalReason, setDisapprovalReason] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const [applicationData, setApplicationData] = useState(null);

  // Dialog open/close handlers
  const handleOpenOfficerDialog = () => setOpenOfficerDialog(true);
  const handleCloseOfficerDialog = () => setOpenOfficerDialog(false);
  const handleOpenContactDialog = () => setOpenContactDialog(true);
  const handleCloseContactDialog = () => setOpenContactDialog(false);

  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedLabTypeId = searchParams.get("labTypeId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;
  const [applicationStage, setApplicationStage] = useState(null);
  const [assessmentTypeId, setAssessmentTypeId] = useState(null);

  // Load dealing officer details (mode 31)
  const loadDealingOfficer = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 31,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setDealingOfficer(response.data[0]); // Take first officer
      }
    } catch (err) {
      console.error("Load dealing officer error:", err);
    }
  }, [appId]);

  const LoadApplicationStage = useCallback(async () => {
    if (!appId) return;
    try {
      const stageResponse = await getApplicationStage(appId);
      if (stageResponse.data.success) {
        setApplicationStage(stageResponse.data.stage);
        setAssessmentTypeId(stageResponse.data.assessmentTypeId);
      }
    } catch (err) {
      console.error("Load application stage error:", err);
    }
  }, [appId]);

  // Load contact person details (mode 27)
  const loadContactPerson = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 27,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setContactPersons(response.data); // Store all contact persons (lab head + CEO details)
      }
    } catch (err) {
      console.error("Load contact person error:", err);
    }
  }, [appId]);

  // Load CAB details on page load (equivalent to .NET getCABDetailAll)
  const loadCABDetails = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get CAB details (mode 100 equivalent)
      const response = await DoApiService.getCABDetails({
        appId: parseFloat(appId), // Convert to double as per .NET code
      });

      if (response.success) {
        setCabDetails(response.data);

        // Store in session equivalent (localStorage)
        localStorage.setItem("AppDashboard_AppId", appId);
        localStorage.setItem("AppDashboard_OrgId", orgId || "");
        localStorage.setItem("AppDashboard_LabTypeId", labTypeId || "");

        // Load additional details
        await Promise.all([loadDealingOfficer(), loadContactPerson()]);
      } else {
        throw new Error(response.message || "Failed to load CAB details");
      }
    } catch (err) {
      setError(`Error loading CAB details: ${err.message}`);
      console.error("Load CAB details error:", err);
    } finally {
      setLoading(false);
    }
  }, [appId, orgId, labTypeId, loadDealingOfficer, loadContactPerson]);

  // Generate and save all PDFs (Certificate, Scope, SCF Annexure)
  const generateAndSaveAllPDFs = useCallback(async () => {
    try {
      console.log("📄 Generating all PDFs...");

      // Fetch all required data
      const [
        certificatemainDataResponse,
        locationResponse,
        applicationResponse,
        certificateResponse,
        scfResponse,
        latestAppIdResponse,
      ] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetMaincertificatedata?appid=${appId}&labtypeid=${cabDetails?.LabTypeId || 3}`,
        ),
        axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CertificateLocation?appid=${appId}&check=nablgvfdcrtyfbskgirwsoh`,
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`,
        ),
        axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getcertificatedata?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`,
        ),
        axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetSCFscope?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`,
        ),
      ]);

      const mainData = certificatemainDataResponse.data || {};
      const locationData = locationResponse.data?.certlocation?.[0] || {};
      const appData = applicationResponse.data?.data || {};
      const certificateData = certificateResponse.data?.certificate || [];
      const scfData = scfResponse.data?.scope || [];

      // Validate data existence
      if (!mainData || !locationData || !appData) {
        throw new Error("Missing required data for PDF generation");
      }

      console.log("✅ Data fetched successfully");

      // 1. GENERATE AND SAVE CERTIFICATE OF ACCREDITATION
      console.log("1️⃣ Generating Certificate of Accreditation PDF...", mainData);
      const certData = {
        labName: locationData.labName || cabDetails?.laboratoryName || "",
        address: mainData.address || "",
        additionaldata: mainData.additionaldata || "",
        applicationNo: mainData.applicationNo || "",
        certificateNumber: locationData.cert_No || "",
        oldCertificateNumber: mainData.oldCertificateNumber || "",
        issueDate: mainData.issuedate || "",
        validUntil: mainData.validtill || "",
        amendmentDate: locationData.amendmentDate || "",
        legalEntity: mainData?.legalEntity || "",
        field: mainData?.field || "",
        standard: locationData?.standard || "-",
        directorname: mainData?.directorname || "",
        directorsignature: mainData?.directorsignature || "",
        ceoname: mainData?.ceoname || "",
        appId: appId,
        orgId: orgId,
        stage: '7d',
        records: [],
      };

      const { pdfBlob: certBlob, fileName: certFileName } =
        await generateCertificateOfAccreditation(certData);
      console.log("✅ Certificate PDF generated");

      const certFormData = new FormData();
      certFormData.append("file", certBlob, certFileName);
      const certUploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/upload`,
        certFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (!certUploadResponse.data.success)
        throw new Error("Failed to upload certificate");
      const certUrl = certUploadResponse.data.data.publicUrl;
      const orgIdFromStorage = localStorage.getItem("orgId");
      await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveletters?appid=${appId}&URL=${certUrl}&updatedby=${orgIdFromStorage}&type=Main`,
      );
      console.log("✅ Certificate saved to database");

      // 2. GENERATE AND SAVE SCOPE OF ACCREDITATION
      if (certificateData && certificateData.length > 0) {
        console.log("2️⃣ Generating Scope of Accreditation PDF...");
        const transformedRecords = certificateData.map((record) => ({
          discipline: record.disciplineName || "",
          productMaterial: record.productMaterial || "",
          specificTest: record.specifictest || "",
          scopeYear: record.scopeyear || "",
          unit: record.unit || "",
          measr: record.measr || "",
          heading1: record.heading1 || "",
          heading2: record.heading2 || "",
          heading3: record.heading3 || "",
          heading4: record.heading4 || "",
          heading5: record.heading5 || "",
          heading6: record.heading6 || "",
          groupName: record.groupName || "",
          testcode: record.testcode || "",
        }));

        const scopeDataObj = {
          labName: locationData.labName || appData?.LabName || appData?.Organization_Name || cabDetails?.laboratoryName || "",
          location: locationData.location || appData?.Address || "",
          address: mainData.address || "",
          standard: locationData.standard || "-",
          certificateNumber: locationData.cert_No || "",
          issueDate: locationData.issue_Date || "",
          validUntil: locationData.validuntill || "",
          amendmentDate: appData?.AssessmentTypeId === 1 ? "": locationData.amendmentDate ? locationData.amendmentDate : "",
          headerChange: locationData.isNew === "1",
          legalEntity: appData?.LabName || appData?.Organization_Name || "",
          additionaldata: mainData.additionaldata || "",
          applicationNo: mainData.applicationNo || "",
          field:
            appData?.LabTypeId === 1
              ? "TESTING"
              : appData?.LabTypeId === 2
                ? "CALIBRATION"
                : appData?.LabTypeId === 3
                  ? "MEDICAL"
                  : appData?.LabTypeId === 4
                    ? "PTP"
                    : appData?.LabTypeId === 5
                      ? "RMP"
                      : "",
          records: transformedRecords,
          labType: locationData.labType || appData?.LabType,
          labTypeId: locationData.LabTypeId || appData?.LabTypeId,
          validity: locationData.validity || "",
          permanent: locationData.permanent === "1",
          mobile: locationData.mobile === "1",
          site: locationData.site === "1",
          potc: locationData.potc === "1",
          appId: appId,
          orgId: orgId,
        };

        const { pdfBlob: scopeBlob, fileName: scopeFileName } =
          await generateScopeOfAccreditation(scopeDataObj);
        console.log("✅ Scope PDF generated");

        const scopeFormData = new FormData();
        scopeFormData.append("file", scopeBlob, scopeFileName);
        const scopeUploadResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/files/upload`,
          scopeFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (!scopeUploadResponse.data.success)
          throw new Error("Failed to upload scope");
        const scopeUrl = scopeUploadResponse.data.data.publicUrl;

        await axios.post(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/savescopeurl?appid=${appId}&scopeurl=${scopeUrl}&updatedby=${orgIdFromStorage}`,
        );
        console.log("✅ Scope saved to database");
      } else {
        console.log("ℹ️ No certificate data available for Scope generation");
      }

      // 3. GENERATE AND SAVE SCF ANNEXURE
      if (scfData && scfData.length > 0) {
        console.log("3️⃣ Generating SCF Annexure PDF...");
        const scfCertData = {
          labName: locationData.labName || cabDetails?.laboratoryName || "",
          address: mainData.address || "",
          additionaldata: mainData.additionaldata || "",
          applicationNo: mainData.applicationNo || "",
          certificateNumber: locationData.cert_No || "",
          issueDate: mainData.issuedate ? mainData.issuedate : "",
          validUntil: mainData.validtill ? mainData.validtill : "",
          amendmentDate: appData?.AssessmentTypeId === 1 ? "" : locationData.amendmentDate ? locationData.amendmentDate : "",
          legalEntity: mainData?.legalEntity || "",
          field: mainData?.field || "",
          standard: locationData?.standard || "-",
          directorname: mainData?.directorname || "",
          directorsignature: mainData?.directorsignature || "",
          ceoname: mainData?.ceoname || "",
          appId: appId,
          orgId: orgId,
          records: [],
        };

        const scfResult = await generateSCFAnnexure(scfCertData, scfData);
        if (!scfResult.success) throw new Error(scfResult.message);

        const { pdfBlob: scfBlob, fileName: scfFileName } = scfResult;
        console.log("✅ SCF Annexure PDF generated");

        const scfFormData = new FormData();
        scfFormData.append("file", scfBlob, scfFileName);
        const scfUploadResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/files/upload`,
          scfFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (!scfUploadResponse.data.success)
          throw new Error("Failed to upload SCF");
        const scfUrl = scfUploadResponse.data.data.publicUrl;

        await axios.post(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/savescfscopeurl?appid=${appId}&scopeurl=${scfUrl}&updatedby=${orgIdFromStorage}`,
        );
        console.log("✅ SCF Annexure saved to database");
      } else {
        console.log("ℹ️ No SCF data available for Annexure generation");
      }

      console.log(
        "✅✅✅ All available PDFs generated and saved successfully!",
      );
      return true;
    } catch (error) {
      console.error("Error generating/saving PDFs:", error);
      return false;
    }
  }, [appId, orgId, cabDetails]);

  // Handle Approve Click
  const handleApproveClick = () => {
    setApproveDialogOpen(true);
  };

  // Handle Disapprove Click
  const handleDisapproveClick = () => {
    setDisapproveDialogOpen(true);
  };

  // Submit Approve
  const handleApproveSubmit = async () => {
    try {
      if (!ceoPassword.trim()) {
        setError("Please enter password");
        return;
      }

      setApproveLoading(true);

      // Verify password
      const orgIdFromStorage = localStorage.getItem("orgId");
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: ceoPassword,
        },
      );

      if (!passwordResponse.data.success) {
        setError("Invalid password");
        setApproveLoading(false);
        return;
      }

      // Call CEO approve API
      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Ceoapprove`,
        null,
        {
          params: {
            appid: appId,
            orgid: orgIdFromStorage,
            type: "Approve",
            reason: "NA",
          },
        },
      );

      if (response.data.isstatus) {
        setSuccess(
          response.data.message || "Certificate approved successfully",
        );

        // Generate and save all PDFs after approval
        await acProcessService.updateApplicationStage(appId, "7d");
        await generateAndSaveAllPDFs();
        setApproveDialogOpen(false);
        setCeoPassword("");

        const latestappid = await axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetLatestAppid?orgid=${orgId}&token=nablgvfdcrtyfbskgirwsoh`,
        );

        const path = `${process.env.REACT_APP_PAGE_URL}/viewCertificate?appId=${encryptText(latestappid.data.appid)}&orgId=${encryptText(orgId)}&action=${encryptText("download")}`;

        const saveCertificateUrl = await axios.post(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savecertificateurl`,
          {
            appid: latestappid.data.appid,
            URl: path,
            updatedby: orgIdFromStorage,
            type: "MainURL",
          },
        );
        console.log(saveCertificateUrl);
        toast.success("Certificate approved successfully!");

        // Refresh page or redirect
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError(response.data.message || "Failed to approve certificate");
      }
    } catch (error) {
      console.error("Error approving certificate:", error);
      setError(
        error.response?.data?.message ||
          "Error approving certificate. Please try again.",
      );
    } finally {
      setApproveLoading(false);
    }
  };

  // Submit Disapprove
  const handleDisapproveSubmit = async () => {
    try {
      if (!ceoPassword.trim()) {
        setError("Please enter password");
        return;
      }

      setApproveLoading(true);

      // Verify password
      const orgIdFromStorage = localStorage.getItem("orgId");
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: ceoPassword,
        },
      );

      if (!passwordResponse.data.success) {
        setError("Invalid password");
        setApproveLoading(false);
        return;
      }

      // Call CEO approve API (same endpoint for disapprove)
      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Ceoapprove`,
        null,
        {
          params: {
            appid: appId,
            orgid: orgId,
            type: "Disapprove",
            reason: disapprovalReason,
          },
        },
      );

      if (response.data.isstatus) {
        setSuccess(
          response.data.message || "Certificate disapproved successfully",
        );
        setDisapproveDialogOpen(false);
        setCeoPassword("");
        setDisapprovalReason("");

        // Refresh page or redirect
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(response.data.message || "Failed to disapprove certificate");
      }
    } catch (error) {
      console.error("Error disapproving certificate:", error);
      setError(
        error.response?.data?.message ||
          "Error disapproving certificate. Please try again.",
      );
    } finally {
      setApproveLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    loadCABDetails();
    LoadApplicationStage();
  }, [loadCABDetails, LoadApplicationStage]);

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Handle View Communication Letter
  const handleViewCommunicationLetter = async () => {
    try {
      toast.info("Fetching communication letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load communication data and get it directly (don't rely on state)
      const comminicationData = await loadCommunicationData(appId, orgId);

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: comminicationData.date
          ? new Date(comminicationData.date).toLocaleDateString("en-GB")
          : "",
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: comminicationData.subject || "N/A",
        body: comminicationData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateCommunicationLetter(letterData);
      toast.success("Communication letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate communication letter");
    }
  };

  // Handle View Decision Letter
  const handleViewDecisionLetter = async () => {
    try {
      toast.info("Fetching decision letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load decision data and get it directly (don't rely on state)
      const decisionData = await loadDecisionData(appId, orgId);

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: decisionData.date
          ? new Date(decisionData.date).toLocaleDateString("en-GB")
          : "",
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: decisionData.subject || "N/A",
        body: decisionData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateDecisionLetter(letterData);
      toast.success("Decision letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate decision letter");
    }
  };

  const handleDownloadCertificate = () => {
    window.open(
      `/main/viewCertificate?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&action=${encryptText("download")}`,
      "_blank",
    );
  };

  const decisionProcess = async () => {
     const url = `/main/ac-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}`;
     navigate(url);
    };

  // Load application details for communication letter
  const loadApplicationDetails = async (appId) => {
    try {
      const [appDetailsResponse, labHeadResponse, doResponse] =
        await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`,
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 27, appId: appId },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 31, appId: appId },
          }),
        ]);

      const appData = appDetailsResponse.data?.data || {};
      const labHeadData = labHeadResponse.data?.data?.[0] || {};
      const doData = doResponse.data?.data?.[0] || {};

      const appDetails = {
        ApplicationNo: appData.ApplicationNo || "N/A",
        LaboratoryName: appData.LaboratoryName || "N/A",
        ContactPerson: labHeadData.name || "N/A",
        Address: labHeadData.address || "N/A",
        ExtAddress:
          `${labHeadData.city || "N/A"} ${labHeadData.state || "N/A"} ${labHeadData.pincode || "N/A"}`.trim(),
        ContactMobile: labHeadData.mobile || "N/A",
        ContactEmail: labHeadData.email || "N/A",
        DoName: doData.name || "N/A",
        DoEmail: doData.email || "N/A",
        CommunicationLetterSent: appData.CommunicationLetterSent || 0,
      };

      setApplicationData(appDetails);
      return appDetails;
    } catch (err) {
      console.error("Error loading application details:", err);
      return null;
    }
  };

  // Load decision data
  const loadDecisionData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getdecision`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date,
      };
    } catch (err) {
      console.error("Error loading decision data:", err);
      return {
        subject: "",
        body: "",
        date: "",
      };
    }
  };

  // Load communication data
  const loadCommunicationData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetCommunication`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date,
      };
    } catch (err) {
      console.error("Error loading communication data:", err);
      return {
        subject: "",
        body: "",
        date: "",
      };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading CEO Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!cabDetails) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No CAB details found for this application
        </Typography>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header with Gradient Background */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Applications
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Dashboard
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DashboardIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {cabDetails.laboratoryName}
                </Typography>

                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {cabDetails.applicationNo} • {cabDetails.labType} •
                  <Chip
                    label={`Standard: ${cabDetails.standard}`}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                    }}
                  />
                  <Chip
                    icon={
                      <Person2Outlined sx={{ color: "white !important" }} />
                    }
                    label="Contact Persons"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenContactDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                  <Chip
                    icon={
                      <Person2Outlined sx={{ color: "white !important" }} />
                    }
                    label="Dealing Officer"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenOfficerDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 2 }}>
        {/* Key Metrics Cards */}
        <Grid container spacing={3}>
          {/* Current Stage */}
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, #fe8f4fff 0%, #fe3b00ff 100%)",
                color: "white",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(79, 172, 254, 0.25)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 40px rgba(79, 172, 254, 0.35)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <ScheduleIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}
                >
                  Current Stage
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {cabDetails.stage || "N/A"} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Last Updated
                  </Typography>{" "}
                  {formatDate(cabDetails.lastUpdated)} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Pending At
                  </Typography>{" "}
                  {cabDetails.PendingAt || "N/A"}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Submission Date
                  </Typography>{" "}
                  {formatDate(cabDetails.submissionDate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "success.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <DescriptionIcon />
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Quick Actions
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ScrutinyIcon />}
                      onClick={() => {
                        const params = new URLSearchParams({
                          appId: encryptedAppId,
                          orgId: encryptedOrgId,
                          labTypeId: encryptedLabTypeId,
                        });
                        navigate(
                          `/main/ceo/application/form8?${params.toString()}`,
                        );
                      }}
                    >
                      Form 8
                    </Button>
                    {applicationStage && applicationStage === "20" && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ScrutinyIcon />}
                        onClick={() => {
                          const params = new URLSearchParams({
                            appId: encryptedAppId,
                            orgId: encryptedOrgId,
                            labTypeId: encryptedLabTypeId,
                          });
                          navigate(
                            `/main/ceo/application/form9?${params.toString()}`,
                          );
                        }}
                      >
                        Form 9
                      </Button>
                    )}
                    {applicationStage && applicationStage === "7c" && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={handleApproveClick}
                        >
                          Approve Certificate
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={handleDisapproveClick}
                        >
                          Disapprove Certificate
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewCommunicationLetter}
                        >
                          View Communication Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewDecisionLetter}
                        >
                          View Decision Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadCertificate}
                        >
                          Download Certificate
                        </Button>
                        {assessmentTypeId === 22 && (
                          <Button
                            variant="contained"
                            startIcon={<DescriptionIcon />}
                            onClick={() => decisionProcess()}
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              backgroundColor: "#2e7d32",
                              "&:hover": { backgroundColor: "#1b5e20" },
                            }}
                          >
                            Decision Process
                          </Button>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Dealing Officer Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Dealing Officer Information */}
            <Dialog
              open={openOfficerDialog}
              onClose={handleCloseOfficerDialog}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Dealing Officer Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BadgeIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Dealing Officer
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Officer Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.name || "Not assigned"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.email || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Contact
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.mobile ||
                          dealingOfficer?.email ||
                          "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseOfficerDialog}
                  color="primary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>

          {/* Contact Person Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Contact Person Information */}
            <Dialog
              open={openContactDialog}
              onClose={handleCloseContactDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Contact Person Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "secondary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Laboratory Contacts
                  </Typography>
                </Box>

                {contactPersons.length > 0 ? (
                  <Grid container spacing={3}>
                    {contactPersons.map((contact, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: "100%",
                            border: contact.isPrimary
                              ? "2px solid #1976d2"
                              : "1px solid #e0e0e0",
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor:
                                    contact.recordType === "lab_head_contact"
                                      ? "primary.main"
                                      : "success.main",
                                  mr: 2,
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <Person2Outlined sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {contact.recordType === "lab_head_contact"
                                    ? "Contact Person Details"
                                    : "CEO Details"}
                                </Typography>
                                {/* {contact.isPrimary && (
                                  <Chip 
                                    label="Primary" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ mt: 0.5 }} 
                                  />
                                )} */}
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Name
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, mt: 0.5 }}
                              >
                                {contact.name || "Not available"}
                              </Typography>
                            </Box>

                            {contact.email && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Email
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.email}
                                </Typography>
                              </Box>
                            )}

                            {contact.mobile && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Mobile
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.mobile}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No contact information available
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseContactDialog}
                  color="secondary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>

        {/* Assessor Details Section */}
        <Grid item xs={12}>
          <AssessorDetailsView
            appId={encryptedAppId}
            orgId={encryptedOrgId}
            type="JD"
            stage={cabDetails.CurrentStage}
          />
        </Grid>

        {/* Reports Details Section */}
        <ReportsDetails appId={appId} orgId={orgId} />

        {/* Payment Details Section */}
        <PaymentDetails appId={appId} />

        {/* Email Notification Details Section */}
        <EmailNotificationDetails appId={appId} isCab={false} />

        {/* CEO Approve Dialog */}
        <Dialog
          open={approveDialogOpen}
          onClose={() => !approveLoading && setApproveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon color="success" />
              Approve Certificate
              {approveLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* {cabDetails && (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Application No: {cabDetails.applicationNo}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Lab: {cabDetails.laboratoryName}
                  </Typography>
                </Box>
              )} */}

              <TextField
                fullWidth
                type="password"
                label="Enter Your Password"
                value={ceoPassword}
                onChange={(e) => setCeoPassword(e.target.value)}
                disabled={approveLoading}
                required
                helperText="Enter your password to confirm approval"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setApproveDialogOpen(false);
                setCeoPassword("");
              }}
              disabled={approveLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveSubmit}
              variant="contained"
              color="success"
              disabled={approveLoading || !ceoPassword.trim()}
              startIcon={
                approveLoading ? <CircularProgress size={20} /> : <CheckIcon />
              }
            >
              {approveLoading ? "Approving..." : "Approve"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* CEO Disapprove Dialog */}
        <Dialog
          open={disapproveDialogOpen}
          onClose={() => !approveLoading && setDisapproveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CloseIcon color="error" />
              Disapprove Certificate
              {approveLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* {cabDetails && (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Application No: {cabDetails.applicationNo}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Lab: {cabDetails.laboratoryName}
                  </Typography>
                </Box>
              )} */}

              <TextField
                fullWidth
                type="password"
                label="Enter Your Password"
                value={ceoPassword}
                onChange={(e) => setCeoPassword(e.target.value)}
                disabled={approveLoading}
                required
                helperText="Enter your password to confirm disapproval"
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Disapproval"
                placeholder="Enter the reason for disapproving this certificate"
                value={disapprovalReason}
                onChange={(e) => setDisapprovalReason(e.target.value)}
                disabled={approveLoading}
                required
                helperText="Please provide a detailed reason for disapproval"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDisapproveDialogOpen(false);
                setCeoPassword("");
                setDisapprovalReason("");
              }}
              disabled={approveLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisapproveSubmit}
              variant="contained"
              color="error"
              disabled={
                approveLoading ||
                !ceoPassword.trim() ||
                !disapprovalReason.trim()
              }
              startIcon={
                approveLoading ? <CircularProgress size={20} /> : <CloseIcon />
              }
            >
              {approveLoading ? "Disapproving..." : "Disapprove"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AppDashboard;
